<template>
  <div class="order-detail">
    <div class="order-detail__shell">
      <nav class="order-detail__crumb">
        <router-link to="/mall">함우리몰</router-link>
        <AppIcon icon="mdi-chevron-right" :size="16" />
        <router-link to="/mall/orders">주문내역</router-link>
        <AppIcon icon="mdi-chevron-right" :size="16" />
        <span>{{ code }}</span>
      </nav>

      <div v-if="loading" class="order-detail__loading">
        <AppIcon icon="mdi-loading" :size="40" />
        <p>주문을 불러오는 중입니다…</p>
      </div>

      <div v-else-if="!order" class="order-detail__loading">
        <AppIcon icon="mdi-file-search-outline" :size="48" />
        <p>주문을 찾을 수 없습니다.</p>
        <router-link :to="MALL_ORDERS_PATH" class="order-detail__link">주문내역으로</router-link>
      </div>

      <div v-else class="order-detail__layout">
        <div class="order-detail__main">
          <!-- 결제 상태 -->
          <section
            class="order-detail__card order-detail__status"
            :class="
              order.status === 'PENDING' && order.payNotifiedAt
                ? 'is-confirming'
                : `is-${order.status.toLowerCase()}`
            "
          >
            <div class="order-detail__status-icon">
              <AppIcon :icon="statusIcon" :size="26" />
            </div>
            <div class="order-detail__status-body">
              <p class="order-detail__status-label">{{ mallBuyerOrderStatusLabel(order) }}</p>
              <p class="order-detail__status-desc">{{ statusDesc }}</p>
              <p
                v-if="order.status === 'PENDING' && !order.payNotifiedAt"
                class="order-detail__expire-note"
                :class="{
                  'is-warn': pendingExpireTone(order.orderedAt) === 'warn',
                  'is-alert': pendingExpireTone(order.orderedAt) === 'alert',
                }"
              >
                {{ pendingExpireLabel(order.orderedAt) }}
                · {{ PENDING_EXPIRE_DAYS }}일 안에 {{ order.payMethod === 'TOSS' ? '결제' : '입금' }}하지 않으면 주문이 자동 취소됩니다
              </p>
              <p
                v-else-if="order.status === 'PENDING' && order.payNotifiedAt"
                class="order-detail__expire-note"
              >
                입금 알림을 보냈습니다. 운영팀이 통장을 확인 중입니다.
              </p>
            </div>
            <dl class="order-detail__status-meta">
              <div>
                <dt>주문번호</dt>
                <dd>{{ order.code }}</dd>
              </div>
              <div>
                <dt>주문일시</dt>
                <dd>{{ formatDateTime(order.orderedAt) }}</dd>
              </div>
              <div v-if="order.paidAt">
                <dt>확인일시</dt>
                <dd>{{ formatDateTime(order.paidAt) }}</dd>
              </div>
            </dl>
            <router-link
              :to="`/mall/mypage/consults/write?order=${encodeURIComponent(order.code)}`"
              class="order-detail__inquiry-link no-print"
            >
              <AppIcon icon="mdi-forum-outline" :size="15" />
              이 주문 문의하기
            </router-link>
          </section>

          <section
            v-if="order.status === 'PENDING' && order.payMethod === 'TOSS'"
            class="order-detail__pay"
          >
            <header class="order-detail__pay-head">
              <div>
                <h2>이어서 결제</h2>
                <p>카드 또는 간편결제로 남은 금액을 냅니다.</p>
              </div>
              <strong>{{ formatPrice(order.totalAmount) }}원</strong>
            </header>
            <template v-if="tossEnabled">
              <div id="order-toss-method" class="order-detail__pay-frame"></div>
              <div id="order-toss-agreement" class="order-detail__pay-frame"></div>
              <p class="order-detail__pay-note">
                {{ payNotice }}
              </p>
              <p v-if="tossTestMode" class="order-detail__pay-note is-test">
                테스트 결제입니다. 승인해도 카드에서 금액이 빠지지 않습니다.
              </p>
              <button
                type="button"
                class="order-detail__pay-go"
                :disabled="payButtonDisabled"
                @click="onPayAction"
              >
                {{ payButtonLabel }}
              </button>
            </template>
            <p v-else class="order-detail__pay-note">
              결제창을 불러오지 못했습니다. 잠시 후 새로고침해 주세요.
            </p>
          </section>

          <section
            v-if="showFulfillment"
            class="order-detail__card order-detail__timeline no-print"
            aria-label="주문 진행 상태"
          >
            <ol class="order-detail__steps">
              <li
                v-for="(step, index) in timelineSteps"
                :key="step.key"
                class="order-detail__step"
                :class="{
                  'is-done': index < timelineIndex,
                  'is-current': index === timelineIndex,
                }"
              >
                <span class="order-detail__step-dot">
                  <AppIcon v-if="index < timelineIndex" icon="mdi-check" :size="14" />
                  <em v-else>{{ index + 1 }}</em>
                </span>
                <span class="order-detail__step-label">{{ step.label }}</span>
              </li>
            </ol>
            <p class="order-detail__timeline-note">{{ timelineNote }}</p>
          </section>

          <!-- 주문 내역 -->
          <section class="order-detail__card">
            <h2>주문 내역 <em>{{ order.items?.length ?? 0 }}건</em></h2>
            <ul class="order-detail__items">
              <li v-for="item in order.items" :key="item.idx">
                <img v-if="item.image" :src="item.image" :alt="item.name" loading="lazy" />
                <span v-else class="order-detail__noimg">
                  <AppIcon icon="mdi-image-off-outline" :size="18" />
                </span>
                <div class="order-detail__item-body">
                  <p class="order-detail__item-name">{{ item.name }}</p>
                  <p class="order-detail__item-sub">
                    <template v-if="item.option">{{ item.option }} · </template>
                    <template v-if="item.storeName">{{ item.storeName }} · </template>
                    {{ item.qty }}{{ sellNoun(item) }}
                  </p>
                  <p v-if="frozenTerms(item)" class="order-detail__item-policy">
                    {{ frozenTerms(item) }}
                  </p>
                  <MallShipmentLedger
                    v-if="showFulfillment && (item.invoice || item.shipments?.length)"
                    :item="item"
                    lookup-tour="mall-order-tracking"
                    @track="openItemShipment(item, $event)"
                  />
                </div>
                <div class="order-detail__item-right">
                  <strong>{{ formatPrice(item.amount) }}원</strong>
                  <span
                    v-if="showFulfillment"
                    class="order-detail__item-status"
                    :class="`is-${item.status.toLowerCase()}`"
                  >
                    {{ MALL_ORDER_ITEM_STATUS_LABEL[item.status] }}
                  </span>
                  <button
                    v-if="showFulfillment && canConfirm(item.status) && claimForm.idx !== item.idx"
                    type="button"
                    class="order-detail__claim-btn order-detail__claim-btn--primary"
                    :disabled="confirming === item.idx"
                    @click="onConfirmPurchase(item.idx)"
                  >
                    {{ confirming === item.idx ? '처리 중…' : '구매확정' }}
                  </button>
                  <button
                    v-if="showFulfillment && canClaimRefund(item.status) && claimForm.idx !== item.idx"
                    type="button"
                    class="order-detail__claim-btn"
                    @click="openClaim(item.idx, 'CANCEL')"
                  >
                    환불신청
                  </button>
                  <button
                    v-if="showFulfillment && canClaimReturn(item.status) && claimForm.idx !== item.idx"
                    type="button"
                    class="order-detail__claim-btn"
                    @click="openClaim(item.idx, 'RETURN')"
                  >
                    반품 요청
                  </button>
                  <button
                    v-if="showFulfillment && canClaimFault(item.status) && claimForm.idx !== item.idx"
                    type="button"
                    class="order-detail__claim-btn"
                    @click="openClaim(item.idx, 'RETURN', true)"
                  >
                    하자·오배송 반품
                  </button>
                  <p
                    v-if="
                      showFulfillment &&
                      item.status === 'SHIPPING' &&
                      claimForm.idx !== item.idx
                    "
                    class="order-detail__claim-reason"
                  >
                    배송 중에는 환불신청할 수 없습니다. 받으신 뒤 반품으로 요청해 주세요.
                  </p>
                  <router-link
                    v-if="showFulfillment && canReview(item) && claimForm.idx !== item.idx"
                    :to="`/mall/${item.productCode}?tab=review`"
                    class="order-detail__claim-btn"
                  >
                    후기 작성
                  </router-link>
                  <p
                    v-if="
                      showFulfillment &&
                      (item.status === 'CANCEL_REQUEST' || item.status === 'RETURN_REQUEST')
                    "
                    class="order-detail__claim-reason"
                  >
                    판매업체 확인 대기 중 · {{ item.claimReason }}
                  </p>
                  <p
                    v-else-if="
                      showFulfillment && item.claimReason && item.status === 'CANCELED'
                    "
                    class="order-detail__claim-reason"
                  >
                    승인됨 · {{ item.claimReason }}
                  </p>
                  <p
                    v-else-if="showFulfillment && item.claimRejectReason"
                    class="order-detail__claim-reason"
                  >
                    반려됨 · {{ item.claimRejectReason }}
                  </p>
                  <p
                    v-else-if="
                      showFulfillment && item.status === 'DELIVERED' && item.deliveredAt
                    "
                    class="order-detail__claim-reason"
                  >
                    {{ autoConfirmLabel(item.deliveredAt) }}. 그 전에 구매확정을 누를 수도 있습니다.
                  </p>
                </div>

                <form
                  v-if="showFulfillment && claimForm.idx === item.idx"
                  class="order-detail__panel"
                  @submit.prevent="submitClaim"
                >
                  <header class="order-detail__panel-head">
                    <strong>{{ claimForm.type === 'RETURN' ? '반품 요청' : '환불신청' }}</strong>
                    <span>환불 예정 {{ formatPrice(claimRefundPreview(item)) }}원</span>
                  </header>
                  <fieldset v-if="claimForm.type === 'RETURN'" class="order-detail__cause">
                    <legend>반품 구분</legend>
                    <label v-if="!claimForm.faultOnly">
                      <input v-model="claimForm.returnCause" type="radio" value="CHANGE" />
                      <span>단순 변심 · 반품 배송비 {{ formatPrice(item.returnFee || 0) }}원 차감</span>
                    </label>
                    <label>
                      <input v-model="claimForm.returnCause" type="radio" value="FAULT" />
                      <span>상품 하자·오배송 · 상품 금액 전액</span>
                    </label>
                  </fieldset>
                  <label class="order-detail__field">
                    <span>요청 사유</span>
                    <textarea
                      v-model.trim="claimForm.reason"
                      rows="3"
                      maxlength="255"
                      placeholder="사유를 구체적으로 적어 주세요."
                    />
                  </label>
                  <div v-if="order.payMethod !== 'TOSS'" class="order-detail__field-grid">
                    <label class="order-detail__field">
                      <span>은행</span>
                      <select v-model="claimForm.refundBank">
                        <option value="" disabled>선택</option>
                        <option v-for="bank in MALL_BANKS" :key="bank" :value="bank">{{ bank }}</option>
                      </select>
                    </label>
                    <label class="order-detail__field">
                      <span>계좌번호</span>
                      <input
                        v-model.trim="claimForm.refundAccount"
                        type="text"
                        inputmode="numeric"
                        maxlength="50"
                        placeholder="숫자만 입력"
                      />
                    </label>
                    <label class="order-detail__field">
                      <span>예금주</span>
                      <input
                        v-model.trim="claimForm.refundHolder"
                        type="text"
                        maxlength="50"
                        :placeholder="orderDepositor || '예금주명'"
                      />
                      <em v-if="showClaimHolderHint" class="order-detail__field-hint">
                        주문 때 적은 입금자명입니다. 환불 받을 통장의 예금주와 같은지 확인해 주세요.
                      </em>
                    </label>
                  </div>
                  <p class="order-detail__panel-note">
                    {{
                      order.payMethod === 'TOSS'
                        ? claimForm.type === 'CANCEL'
                          ? '배송 시작 전까지만 환불신청할 수 있습니다. 신청하면 주문이 바로 취소되고, 결제했던 카드·간편결제로 환불됩니다. 그 판매업체 상품이 이것까지면 그 업체 출고 배송비도 함께 취소됩니다.'
                          : '승인되면 위에 적힌 금액이 결제했던 카드·간편결제로 돌아갑니다. 단순 변심은 반품 배송비를 빼고, 하자·오배송은 상품 금액 전액입니다. 처음 낸 출고 배송비는 반품에서 빼지 않습니다.'
                        : claimForm.type === 'CANCEL'
                          ? '입금 확인된 주문은 배송 시작 전까지만 환불신청할 수 있습니다. 신청하면 주문이 바로 취소되고, 운영팀이 위에 적힌 금액으로 환불합니다. 그 판매업체 상품이 이것까지면 그 업체 출고 배송비도 포함됩니다. 이 계좌는 회원 환불계좌로 저장됩니다.'
                          : `${claimReturnNote} 이 계좌는 회원 환불계좌로 저장됩니다.`
                    }}
                  </p>
                  <div class="order-detail__claim-form-actions">
                    <button type="button" class="order-detail__ghost" @click="closeClaim">닫기</button>
                    <button
                      type="submit"
                      class="order-detail__claim-submit"
                      :disabled="claiming === item.idx || !canSubmitClaim"
                    >
                      {{
                        claiming === item.idx
                          ? '처리 중…'
                          : claimForm.type === 'CANCEL'
                            ? '환불신청하기'
                            : '요청 보내기'
                      }}
                    </button>
                  </div>
                </form>

                <div
                  v-else-if="
                    showFulfillment &&
                    order.payMethod !== 'TOSS' &&
                    item.refundStatus === 'PENDING' &&
                    !item.refundAccount &&
                    refundForm.idx !== item.idx
                  "
                  class="order-detail__refund-card is-need"
                >
                  <div>
                    <strong>환불 계좌가 필요합니다</strong>
                    <p>환불 예정 {{ formatPrice(item.refundAmount || item.amount) }}원</p>
                  </div>
                  <button type="button" class="order-detail__claim-submit" @click="openRefundAccount(item)">
                    계좌 입력
                  </button>
                </div>

                <form
                  v-else-if="showFulfillment && refundForm.idx === item.idx"
                  class="order-detail__panel"
                  @submit.prevent="submitRefundAccount"
                >
                  <header class="order-detail__panel-head">
                    <strong>환불 받을 계좌</strong>
                    <span>{{ formatPrice(item.refundAmount || item.amount) }}원</span>
                  </header>
                  <div class="order-detail__field-grid">
                    <label class="order-detail__field">
                      <span>은행</span>
                      <select v-model="refundForm.bank">
                        <option value="" disabled>선택</option>
                        <option v-for="bank in MALL_BANKS" :key="bank" :value="bank">{{ bank }}</option>
                      </select>
                    </label>
                    <label class="order-detail__field">
                      <span>계좌번호</span>
                      <input
                        v-model.trim="refundForm.account"
                        type="text"
                        inputmode="numeric"
                        maxlength="50"
                        placeholder="숫자만 입력"
                      />
                    </label>
                    <label class="order-detail__field">
                      <span>예금주</span>
                      <input
                        v-model.trim="refundForm.holder"
                        type="text"
                        maxlength="50"
                        :placeholder="orderDepositor || '예금주명'"
                      />
                      <em v-if="showRefundHolderHint" class="order-detail__field-hint">
                        주문 때 적은 입금자명입니다. 환불 받을 통장의 예금주와 같은지 확인해 주세요.
                      </em>
                    </label>
                  </div>
                  <div class="order-detail__claim-form-actions">
                    <button type="button" class="order-detail__ghost" @click="refundForm.idx = 0">닫기</button>
                    <button
                      type="submit"
                      class="order-detail__claim-submit"
                      :disabled="
                        refundSaving === item.idx || !refundForm.bank || !refundForm.account || !refundForm.holder
                      "
                    >
                      {{ refundSaving === item.idx ? '저장 중…' : '계좌 저장' }}
                    </button>
                  </div>
                </form>

                <div
                  v-else-if="
                    showFulfillment &&
                    (item.refundStatus === 'PENDING' || item.refundStatus === 'DONE')
                  "
                  class="order-detail__refund-card"
                  :class="item.refundStatus === 'DONE' ? 'is-done' : 'is-pending'"
                >
                  <span class="order-detail__refund-badge">
                    {{ MALL_REFUND_STATUS_LABEL[item.refundStatus] }}
                  </span>
                  <strong>{{ formatPrice(item.refundAmount || item.amount) }}원</strong>
                  <p v-if="item.refundBank">
                    {{ item.refundBank }} {{ item.refundAccount }} · {{ item.refundHolder }}
                  </p>
                </div>
              </li>
            </ul>

            <dl class="order-detail__amount">
              <div>
                <dt>상품금액</dt>
                <dd>{{ formatPrice(order.itemAmount) }}원</dd>
              </div>
              <div>
                <dt>주문 배송비</dt>
                <dd>{{ formatShippingFeeAmount(order.shippingFee) }}</dd>
              </div>
              <div :class="{ 'is-total': !refundRelation.refunded }">
                <dt>{{ chargeAmountLabel }}</dt>
                <dd>{{ formatPrice(order.totalAmount) }}원</dd>
              </div>
              <div v-if="refundRelation.refunded">
                <dt>환불 완료</dt>
                <dd>-{{ formatPrice(refundRelation.refunded) }}원</dd>
              </div>
              <div v-if="refundRelation.refundPending">
                <dt>환불 대기</dt>
                <dd>{{ formatPrice(refundRelation.refundPending) }}원</dd>
              </div>
              <div v-if="refundRelation.refunded" class="is-total">
                <dt>결제 잔액</dt>
                <dd>{{ formatPrice(refundRelation.balance) }}원</dd>
              </div>
            </dl>
          </section>

          <!-- 배송지 -->
          <section class="order-detail__card">
            <h2>배송지</h2>
            <dl class="order-detail__ship">
              <div>
                <dt>받는 분</dt>
                <dd>{{ order.receiver }}</dd>
              </div>
              <div>
                <dt>연락처</dt>
                <dd>{{ order.phone }}</dd>
              </div>
              <div>
                <dt>주소</dt>
                <dd>{{ order.address }} {{ order.addressDetail }}</dd>
              </div>
              <div v-if="order.memo">
                <dt>요청사항</dt>
                <dd>{{ order.memo }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="showTaxCard" class="order-detail__card">
            <h2>세금계산서</h2>

            <template v-if="hasTaxRecord">
              <dl class="order-detail__dl">
                <div>
                  <dt>상태</dt>
                  <dd>
                    <span
                      class="order-detail__tax-status"
                      :class="{
                        'is-requested': currentTaxStatus === 'REQUESTED',
                        'is-done': currentTaxStatus === 'DONE',
                      }"
                    >
                      {{ taxStatusLabel }}
                    </span>
                    <template v-if="currentTaxStatus === 'DONE' && order.taxAt">
                      · {{ formatDateTime(order.taxAt) }}
                    </template>
                  </dd>
                </div>
                <div v-if="order.bizName">
                  <dt>상호</dt>
                  <dd>{{ order.bizName }}</dd>
                </div>
                <div v-if="order.bizNum">
                  <dt>사업자번호</dt>
                  <dd>{{ formatBizNumber(order.bizNum) }}</dd>
                </div>
                <div v-if="order.taxEmail">
                  <dt>수신 이메일</dt>
                  <dd>{{ order.taxEmail }}</dd>
                </div>
                <div v-if="currentTaxStatus === 'DONE' && order.taxDocUrl">
                  <dt>문서</dt>
                  <dd>
                    <a
                      :href="order.taxDocUrl"
                      class="order-detail__link"
                      target="_blank"
                      rel="noopener"
                    >
                      세금계산서 보기
                    </a>
                  </dd>
                </div>
              </dl>
              <p
                v-if="order.status === 'PENDING' && currentTaxStatus === 'REQUESTED'"
                class="order-detail__tax-hint"
              >
                주문할 때 요청해 두었습니다. 입금이 확인된 뒤 발행됩니다.
              </p>
              <p
                v-else-if="currentTaxStatus === 'REQUESTED'"
                class="order-detail__tax-hint"
              >
                운영팀이 확인한 뒤 세금계산서를 발행합니다.
              </p>
            </template>

            <form v-else class="order-detail__tax-form" @submit.prevent="onRequestTax">
              <p v-if="order.taxRejectReason" class="order-detail__tax-reject">
                이전 발행 요청이 반려되었습니다. {{ order.taxRejectReason }}
              </p>
              <p class="order-detail__tax-hint">
                입금이 확인된 주문에 한해 세금계산서 발행을 요청할 수 있습니다.
              </p>
              <TaxInvoiceRequestPanel
                ref="taxPanelRef"
                :show-toggle="false"
                :compact="true"
                v-model:biz-name="taxForm.bizName"
                v-model:biz-num="taxForm.bizNum"
                v-model:email="taxForm.email"
              />
              <button
                type="submit"
                class="order-detail__claim-btn order-detail__claim-btn--primary"
                :disabled="taxRequesting"
              >
                {{ taxRequesting ? '요청 중…' : '발행 요청' }}
              </button>
            </form>
          </section>

        </div>

        <!-- 계좌 안내 + 주문 리모컨 — 스크롤을 내려도 따라온다 -->
        <aside class="order-detail__side no-print">
          <div class="order-detail__account" :class="{ 'is-done': order.status === 'PAID' }">
            <p class="order-detail__account-title">
              <AppIcon :icon="order.payMethod === 'TOSS' ? 'mdi-credit-card-outline' : 'mdi-bank-outline'" :size="18" />
              {{
                order.status === 'PAID'
                  ? '결제'
                  : order.payMethod === 'TOSS'
                    ? '카드 · 간편결제'
                    : '입금 계좌'
              }}
            </p>

            <template v-if="order.status === 'PENDING' && order.payMethod === 'TOSS'">
              <p class="order-detail__account-amount">
                {{ formatPrice(order.totalAmount) }}<em>원</em>
              </p>
              <p class="order-detail__account-notice">
                {{ payNotice }}
              </p>
              <button
                v-if="tossEnabled"
                type="button"
                class="order-detail__notify"
                :disabled="payButtonDisabled"
                @click="onPayAction"
              >
                {{ payButtonLabel }}
              </button>
            </template>

            <template v-else-if="order.status === 'PENDING'">
              <p class="order-detail__account-amount">
                {{ formatPrice(order.totalAmount) }}<em>원</em>
                <button type="button" class="order-detail__mini-copy" @click="copyAmount">
                  {{ copiedField === 'amount' ? '복사됨' : '복사' }}
                </button>
              </p>
              <dl class="order-detail__account-meta is-open">
                <div>
                  <dt>은행</dt>
                  <dd>{{ payment?.bankName }}</dd>
                </div>
                <div>
                  <dt>계좌</dt>
                  <dd>
                    {{ payment?.account }}
                    <button type="button" class="order-detail__mini-copy" @click="copyAccount">
                      {{ copiedField === 'account' ? '복사됨' : '복사' }}
                    </button>
                  </dd>
                </div>
                <div>
                  <dt>예금주</dt>
                  <dd>{{ payment?.holder }}</dd>
                </div>
                <div>
                  <dt>입금자명</dt>
                  <dd>
                    {{ order.depositor || order.receiver }}
                    <button type="button" class="order-detail__mini-copy" @click="copyDepositor">
                      {{ copiedField === 'depositor' ? '복사됨' : '복사' }}
                    </button>
                  </dd>
                </div>
              </dl>
              <p v-if="payment?.notice" class="order-detail__account-notice">{{ payment.notice }}</p>
              <button
                type="button"
                class="order-detail__notify"
                :class="{ 'is-done': Boolean(order.payNotifiedAt) }"
                :disabled="notifying || Boolean(order.payNotifiedAt)"
                @click="onNotifyPayment"
              >
                <AppIcon
                  :icon="order.payNotifiedAt ? 'mdi-check-circle' : 'mdi-hand-coin-outline'"
                  :size="20"
                />
                {{
                  order.payNotifiedAt
                    ? '입금 알림을 보냈습니다'
                    : notifying
                      ? '알리는 중…'
                      : '입금했어요'
                }}
              </button>
              <p class="order-detail__account-notice">
                {{
                  order.payNotifiedAt
                    ? '운영팀이 통장을 확인하면 안내드립니다. 자동 결제가 아닙니다.'
                    : '버튼을 눌러도 자동 출금되지 않습니다. 직접 이체한 뒤 알려 주세요.'
                }}
              </p>
            </template>

            <template v-else-if="order.status === 'PAID'">
              <p class="order-detail__account-done">
                <AppIcon icon="mdi-check-circle" :size="20" />
                {{ order.payMethod === 'TOSS' ? '결제가 완료되었습니다' : '입금이 확인되었습니다' }}
              </p>
              <p class="order-detail__account-amount is-paid">
                {{ formatPrice(order.totalAmount) }}<em>원</em>
              </p>
              <p class="order-detail__account-notice">
                {{
                  order.payMethod === 'TOSS'
                    ? '카드 · 간편결제로 받은 주문입니다.'
                    : '무통장 입금으로 받은 주문입니다.'
                }}
                판매업체가 상품을 준비합니다.
              </p>
            </template>

            <template v-else>
              <p class="order-detail__account-canceled">취소된 주문입니다.</p>
              <p v-if="order.cancelReason" class="order-detail__account-notice">{{ order.cancelReason }}</p>
            </template>
          </div>

          <div class="order-detail__remote">
            <p class="order-detail__remote-title">주문 메뉴</p>
            <button
              v-if="canPrintStatement"
              type="button"
              class="order-detail__remote-btn is-primary"
              @click="onPrint"
            >
              <AppIcon icon="mdi-printer-outline" :size="18" />
              거래명세서 인쇄
            </button>
            <button
              type="button"
              class="order-detail__remote-btn"
              :disabled="reordering"
              @click="onReorder"
            >
              <AppIcon icon="mdi-cart-plus" :size="18" />
              {{ reordering ? '담는 중…' : '같은 상품 다시 담기' }}
            </button>
            <router-link :to="MALL_ORDERS_PATH" class="order-detail__remote-btn">
              <AppIcon icon="mdi-clipboard-list-outline" :size="18" />
              주문내역 전체보기
            </router-link>
            <button
              v-if="order.status === 'PENDING'"
              type="button"
              class="order-detail__remote-btn is-danger"
              :disabled="canceling"
              @click="onCancel"
            >
              <AppIcon icon="mdi-close-circle-outline" :size="18" />
              주문 취소
            </button>
          </div>
        </aside>
      </div>

      <!-- 화면에서는 숨기고, 입금 확인 후 인쇄 시에만 문서 형태로 출력 -->
      <article
        v-if="order && canPrintStatement"
        class="order-detail__statement"
        aria-hidden="true"
      >
        <header class="order-detail__statement-head">
          <div>
            <p class="order-detail__statement-eyebrow">함우리몰 거래명세서</p>
            <h1>{{ order.orgName || '기관명 미확인' }}</h1>
            <p class="order-detail__statement-meta">
              주문번호 {{ order.code }}
              <template v-if="order.poNumber"> · 발주번호 {{ order.poNumber }}</template>
            </p>
            <p class="order-detail__statement-meta">
              주문일시 {{ formatDateTime(order.orderedAt) }}
              <template v-if="order.paidAt"> · {{ order.payMethod === 'TOSS' ? '결제확인' : '입금확인' }} {{ formatDateTime(order.paidAt) }}</template>
            </p>
            <p class="order-detail__statement-meta">
              결제수단 {{ order.payMethod === 'TOSS' ? (order.pgMethod || '카드·간편결제') : '계좌이체' }}
            </p>
          </div>
          <div class="order-detail__statement-stamp">{{ order.payMethod === 'TOSS' ? '결제확인' : '입금확인' }}</div>
        </header>

        <table class="order-detail__statement-table">
          <thead>
            <tr>
              <th>상품</th>
              <th class="is-num">수량</th>
              <th class="is-num">단가</th>
              <th class="is-num">금액</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in order.items" :key="`stmt-${item.idx}`">
              <td>
                <strong>{{ item.name }}</strong>
                <span v-if="item.option">옵션 · {{ item.option }}</span>
                <span v-if="item.storeName">판매 · {{ item.storeName }}</span>
                <span v-if="item.unit">{{ item.unit }}</span>
                <span v-if="frozenTerms(item)">{{ frozenTerms(item) }}</span>
                <span v-if="item.status === 'CANCELED'">취소 · 환불 {{ formatPrice(item.refundAmount || 0) }}원</span>
              </td>
              <td class="is-num">{{ item.qty }}</td>
              <td class="is-num">{{ formatPrice(item.price) }}원</td>
              <td class="is-num">{{ formatPrice(item.amount) }}원</td>
            </tr>
          </tbody>
        </table>

        <dl class="order-detail__statement-totals">
          <div>
            <dt>상품금액</dt>
            <dd>{{ formatPrice(order.itemAmount) }}원</dd>
          </div>
          <div>
            <dt>주문 배송비</dt>
            <dd>{{ formatShippingFeeAmount(order.shippingFee) }}</dd>
          </div>
          <div :class="{ 'is-total': !refundRelation.refunded }">
            <dt>{{ refundRelation.refunded ? '결제 금액' : '합계' }}</dt>
            <dd>{{ formatPrice(order.totalAmount) }}원</dd>
          </div>
          <div v-if="refundRelation.refunded">
            <dt>환불 완료</dt>
            <dd>-{{ formatPrice(refundRelation.refunded) }}원</dd>
          </div>
          <div v-if="refundRelation.refundPending">
            <dt>환불 대기</dt>
            <dd>{{ formatPrice(refundRelation.refundPending) }}원</dd>
          </div>
          <div v-if="refundRelation.refunded" class="is-total">
            <dt>결제 잔액</dt>
            <dd>{{ formatPrice(refundRelation.balance) }}원</dd>
          </div>
        </dl>

        <section class="order-detail__statement-block">
          <h2>배송지</h2>
          <p>
            {{ order.receiver }} · {{ order.phone }}<br />
            {{ order.address }} {{ order.addressDetail }}
            <template v-if="order.memo"><br />요청사항 · {{ order.memo }}</template>
          </p>
        </section>

        <section v-if="hasTaxRecord" class="order-detail__statement-block">
          <h2>세금계산서</h2>
          <p>
            {{ taxStatusLabel }}
            <template v-if="order.bizName"> · {{ order.bizName }}</template>
            <template v-if="order.bizNum"> · {{ formatBizNumber(order.bizNum) }}</template>
            <template v-if="order.taxEmail"><br />수신 · {{ order.taxEmail }}</template>
          </p>
        </section>

        <p class="order-detail__statement-note">
          본 거래명세서는 {{ order.payMethod === 'TOSS' ? '결제 확인된' : '입금 확인된' }} 주문 기준입니다. 재고·배송 상태는 주문 상세에서 확인해 주세요.
        </p>
      </article>
    </div>

    <MallPayHold v-if="payHold" @close="closePayHold" @refresh="refreshPay" />

    <MallTrackingModal
      v-if="trackTarget"
      :item-idx="trackTarget.idx"
      :courier="trackTarget.courier"
      :invoice="trackTarget.invoice"
      :product-name="trackTarget.name"
      :order-code="trackTarget.orderCode"
      @close="onCloseTracking"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallShipmentLedger from '@/components/mall/MallShipmentLedger.vue'
import MallPayHold from '@/components/mall/MallPayHold.vue'
import MallTrackingModal from '@/components/mall/MallTrackingModal.vue'
import TaxInvoiceRequestPanel from '@/components/mall/TaxInvoiceRequestPanel.vue'
import { MALL_BANKS } from '@/constants/banks'
import { MALL_ORDERS_PATH } from '@/constants/mall'
import { formatPrice } from '@/constants/mallProducts'
import { formatShippingFeeAmount } from '@/utils/mallShipping'
import { type ShipmentLedgerRow } from '@/utils/mallShipment'
import { formatBizNumber } from '@/utils/bizNumber'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  mallBuyerOrderStatusLabel,
  mallOrderRefundTotals,
  MALL_REFUND_STATUS_LABEL,
  cancelMyOrder,
  confirmOrderItemPurchase,
  fetchMyOrder,
  fetchTossAccess,
  notifyMyPayment,
  requestMyTaxInvoice,
  requestOrderItemClaim,
  updateOrderItemRefundAccount,
  type ClaimType,
  type MallOrder,
  type MallOrderItem,
  type MallOrderItemStatus,
  type MallPaymentInfo,
} from '@/api/mallOrder'
import { useTossPayment } from '@/composables/useTossPayment'
import { useCart } from '@/composables/useCart'
import { useMemberAuth } from '@/composables/useMemberAuth'
import {
  PENDING_EXPIRE_DAYS,
  autoConfirmLabel,
  pendingExpireLabel,
  pendingExpireTone,
} from '@/utils/mallOpsUx'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useMallToast } from '@/composables/useMallToast'
import { copyToClipboard } from '@/utils/clipboard'

const route = useRoute()
const router = useRouter()
const { addItem } = useCart()
const { member } = useMemberAuth()
const { showSuccess, showError } = useMallToast()
const {
  ready: tossReady,
  testMode: tossTestMode,
  mount: mountToss,
  pay: requestTossPay,
} = useTossPayment()
const tossPaying = ref(false)
const tossEnabled = ref(false)
const tossMountFailed = ref(false)
const payHold = ref(route.query.pay === 'closed')

const payButtonLabel = computed(() => {
  if (tossPaying.value) return '결제창 여는 중…'
  if (tossReady.value) return '결제하기'
  if (tossMountFailed.value) return '새로고침'
  return '결제창 준비 중…'
})

const payButtonDisabled = computed(
  () => tossPaying.value || (!tossReady.value && !tossMountFailed.value),
)

const payNotice = computed(() => {
  if (!tossEnabled.value || tossMountFailed.value) {
    return '결제창을 불러오지 못했습니다. 새로고침하면 다시 결제할 수 있습니다.'
  }
  if (!tossReady.value) return '결제창을 불러오는 중입니다.'
  return '카드, 간편결제, 휴대폰결제를 받습니다. 계좌이체·가상계좌를 고르면 그 결제는 취소됩니다.'
})

const STATEMENT_PRINT_BODY_CLASS = 'is-mall-statement-print'

function onPrint() {
  if (!canPrintStatement.value) return
  document.body.classList.add(STATEMENT_PRINT_BODY_CLASS)
  const cleanup = () => {
    document.body.classList.remove(STATEMENT_PRINT_BODY_CLASS)
    window.removeEventListener('afterprint', cleanup)
  }
  window.addEventListener('afterprint', cleanup)
  window.print()
  window.setTimeout(cleanup, 1500)
}

const code = computed(() => String(route.params.code ?? ''))

function refreshPay() {
  window.location.assign(`/mall/orders/${code.value}`)
}

function closePayHold() {
  payHold.value = false
  if (route.query.pay == null) return
  const nextQuery = { ...route.query }
  delete nextQuery.pay
  router.replace({ query: nextQuery })
}
const order = ref<MallOrder | null>(null)
const payment = ref<MallPaymentInfo | null>(null)
const loading = ref(true)
const canceling = ref(false)
const notifying = ref(false)
const reordering = ref(false)
const claiming = ref(0)
const confirming = ref(0)
const refundSaving = ref(0)
const taxRequesting = ref(false)
const copiedField = ref<'account' | 'amount' | 'depositor' | ''>('')
const refundForm = reactive({ idx: 0, bank: '', account: '', holder: '' })
const taxForm = reactive({ bizName: '', bizNum: '', email: '' })
const taxPanelRef = ref<InstanceType<typeof TaxInvoiceRequestPanel> | null>(null)
const trackTarget = ref<{
  idx: number
  courier?: string
  invoice?: string
  name?: string
  orderCode?: string
} | null>(null)

function openTracking(item: MallOrderItem) {
  trackTarget.value = item
}

function openItemShipment(item: MallOrderItem, row: ShipmentLedgerRow) {
  trackTarget.value = {
    idx: item.idx,
    courier: row.courier,
    invoice: row.invoice,
    name: item.name,
    orderCode: order.value?.code,
  }
}

function onCloseTracking() {
  trackTarget.value = null
  void load()
}

function taxStatusOf(o: MallOrder) {
  return o.taxStatus || (o.taxInvoice ? 'REQUESTED' : 'NONE')
}

const currentTaxStatus = computed(() =>
  order.value ? taxStatusOf(order.value) : 'NONE',
)

/** 입금 확인 후에만 진행단계·송장·클레임 등 이행 UI를 연다 */
const showFulfillment = computed(() => order.value?.status === 'PAID')

const refundRelation = computed(() => {
  const current = order.value
  const totals = mallOrderRefundTotals(current?.items || [])
  const charged = Math.max(0, Number(current?.totalAmount) || 0)
  return {
    ...totals,
    balance: Math.max(0, charged - totals.refunded),
  }
})

const chargeAmountLabel = computed(() => {
  if (order.value?.status === 'PENDING') {
    return order.value.payMethod === 'TOSS' ? '결제할 금액' : '입금할 금액'
  }
  return refundRelation.value.refunded ? '결제 금액' : '합계'
})

/** 거래명세서는 입금 확인된 주문만 출력한다 */
const canPrintStatement = computed(() => order.value?.status === 'PAID')

const hasTaxRecord = computed(() => {
  const status = currentTaxStatus.value
  return status === 'REQUESTED' || status === 'DONE' || Boolean(order.value?.taxInvoice)
})

/** 입금 확인된 주문만 새로 세금계산서를 요청할 수 있다 */
const canRequestTax = computed(
  () =>
    Boolean(order.value) &&
    order.value!.payMethod !== 'TOSS' &&
    order.value!.status === 'PAID' &&
    currentTaxStatus.value === 'NONE',
)

const showTaxCard = computed(() => hasTaxRecord.value || canRequestTax.value)

const taxStatusLabel = computed(() => {
  if (currentTaxStatus.value === 'DONE') return '발행 완료'
  if (currentTaxStatus.value === 'REQUESTED') return '발행 요청'
  return '미요청'
})

function fillTaxFormFromMember() {
  if (!taxForm.bizName) taxForm.bizName = member.value?.orgName || ''
  if (!taxForm.bizNum) taxForm.bizNum = member.value?.bizNum || ''
  if (!taxForm.email) taxForm.email = member.value?.bossEmail || ''
}
const claimForm = reactive<{
  idx: number
  type: ClaimType
  reason: string
  returnCause: '' | 'CHANGE' | 'FAULT'
  faultOnly: boolean
  refundBank: string
  refundAccount: string
  refundHolder: string
}>({
  idx: 0,
  type: 'CANCEL',
  reason: '',
  returnCause: '',
  faultOnly: false,
  refundBank: '',
  refundAccount: '',
  refundHolder: '',
})

const canSubmitClaim = computed(() => {
  if (!claimForm.reason) return false
  if (claimForm.type === 'RETURN' && !claimForm.returnCause) return false
  if (order.value?.payMethod === 'TOSS') return true
  return Boolean(claimForm.refundBank && claimForm.refundAccount && claimForm.refundHolder)
})

function claimRefundPreview(item: MallOrderItem) {
  if (claimForm.type === 'RETURN') {
    if (claimForm.returnCause !== 'CHANGE') return item.amount
    const fee = Math.max(0, Number(item.returnFee) || 0)
    return Math.max(0, item.amount - fee)
  }
  const items = order.value?.items || []
  const openSameSeller = items.filter(
    (row) => row.sellerCode === item.sellerCode && row.status !== 'CANCELED',
  )
  if (openSameSeller.length !== 1 || openSameSeller[0].idx !== item.idx) return item.amount
  const sellerShip = items
    .filter((row) => row.sellerCode === item.sellerCode)
    .reduce((sum, row) => sum + Math.max(0, Number(row.shippingShare) || 0), 0)
  if (sellerShip > 0) return item.amount + sellerShip
  const openAll = items.filter((row) => row.status !== 'CANCELED')
  if (openAll.length === 1) return item.amount + Math.max(0, Number(order.value?.shippingFee) || 0)
  return item.amount
}

/** 입금확인·출고준비까지 환불신청 가능. 배송 시작 뒤에는 반품만 가능하다. */
function canClaimRefund(status: MallOrderItemStatus) {
  return order.value?.status === 'PAID' && (status === 'PAID' || status === 'READY')
}

function canClaimReturn(status: MallOrderItemStatus) {
  return order.value?.status === 'PAID' && status === 'DELIVERED'
}

function canClaimFault(status: MallOrderItemStatus) {
  return order.value?.status === 'PAID' && status === 'DONE'
}

function canConfirm(status: MallOrderItemStatus) {
  return order.value?.status === 'PAID' && status === 'DELIVERED'
}

function canReview(item: MallOrderItem) {
  if (!item.productCode || !item.productCode.startsWith('MP')) return false
  return (
    order.value?.status === 'PAID' &&
    ['PAID', 'READY', 'SHIPPING', 'DELIVERED', 'DONE'].includes(item.status)
  )
}

const REFUND_ACCOUNT_KEY = 'hamuri.mall.lastRefundAccount'

function loadLastRefundAccount() {
  try {
    const raw = localStorage.getItem(REFUND_ACCOUNT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      bank?: string
      account?: string
      holder?: string
    }
    if (!parsed?.bank || !parsed?.account || !parsed?.holder) return null
    return {
      bank: String(parsed.bank),
      account: String(parsed.account),
      holder: String(parsed.holder),
    }
  } catch {
    return null
  }
}

function saveLastRefundAccount(bank: string, account: string, holder: string) {
  try {
    localStorage.setItem(
      REFUND_ACCOUNT_KEY,
      JSON.stringify({ bank, account, holder }),
    )
  } catch {
    /* ignore */
  }
}

function preferRefundAccount() {
  const fromMember = member.value
  if (fromMember?.refundBank && fromMember.refundAccount && fromMember.refundHolder) {
    return {
      bank: fromMember.refundBank,
      account: fromMember.refundAccount,
      holder: fromMember.refundHolder,
    }
  }
  const fromOrder = (order.value?.items || []).find(
    (item) => item.refundBank && item.refundAccount && item.refundHolder,
  )
  if (fromOrder) {
    return {
      bank: fromOrder.refundBank || '',
      account: fromOrder.refundAccount || '',
      holder: fromOrder.refundHolder || '',
    }
  }
  return loadLastRefundAccount()
}

const orderDepositor = computed(() => String(order.value?.depositor || '').trim())
const holderPrefillFromDepositor = ref(false)

const showClaimHolderHint = computed(
  () =>
    holderPrefillFromDepositor.value &&
    Boolean(orderDepositor.value) &&
    claimForm.refundHolder === orderDepositor.value,
)

const showRefundHolderHint = computed(
  () =>
    holderPrefillFromDepositor.value &&
    Boolean(orderDepositor.value) &&
    refundForm.holder === orderDepositor.value,
)

function applyRefundHolder(savedHolder: string) {
  const saved = String(savedHolder || '').trim()
  if (saved) {
    holderPrefillFromDepositor.value = false
    return saved
  }
  const depositor = orderDepositor.value
  holderPrefillFromDepositor.value = Boolean(depositor)
  return depositor
}

const claimReturnNote = ref(
  '승인되면 운영팀이 위 계좌로 수동 입금합니다. PG 자동환불은 없습니다.',
)

function sellNoun(item: { sellUnit?: string }) {
  return item.sellUnit === 'BOX' ? '박스' : '개'
}

function frozenTerms(item: { leadTime?: string; cutoff?: string; returnEvery?: number; returnUnitFee?: number; sellUnit?: string }) {
  const noun = sellNoun(item)
  const every = Math.max(1, Number(item.returnEvery) || 0)
  const unitFee = Math.max(0, Number(item.returnUnitFee) || 0)
  const parts: string[] = []
  if (item.leadTime) parts.push(`출고 ${item.leadTime}`)
  if (item.cutoff) parts.push(`당일 마감 ${item.cutoff}`)
  if (every > 0) {
    parts.push(
      unitFee
        ? `환불 배송비 ${every}${noun}당 ${unitFee.toLocaleString('ko-KR')}원`
        : '환불 배송비 없음',
    )
  }
  return parts.join(' · ')
}

function describeReturnFee(item: {
  qty?: number
  returnEvery?: number
  returnUnitFee?: number
  returnFee?: number
  sellUnit?: string
  returnPolicy?: string
}) {
  const base = '승인되면 운영팀이 위 계좌로 수동 입금합니다. PG 자동환불은 없습니다.'
  const noun = sellNoun(item)
  const every = Math.max(1, Number(item.returnEvery) || 10)
  const unitFee = Math.max(0, Number(item.returnUnitFee) || 0)
  const qty = Math.max(0, Number(item.qty) || 0)
  const policy = String(item.returnPolicy || '').trim()
  const policyNote = policy ? ` 주문 당시 안내: ${policy.slice(0, 500)}` : ''
  if (!unitFee) {
    return `${base} 이 상품은 단순 변심 반품 배송비가 없습니다. 하자·오배송은 상품 금액 전액입니다.${policyNote}`
  }
  const blocks = qty > 0 ? Math.ceil(qty / every) : 1
  const total = (qty > 0 ? blocks * unitFee : Math.max(0, Number(item.returnFee) || unitFee)).toLocaleString('ko-KR')
  return `${base} 단순 변심은 주문 당시 기준 ${every}${noun}당 ${unitFee.toLocaleString('ko-KR')}원입니다. ${every}${noun} 이하는 한 번이고, 이번 ${qty}${noun}는 ${blocks} × ${unitFee.toLocaleString('ko-KR')}원 = ${total}원을 뺍니다. 하자·오배송은 상품 금액 전액입니다.${policyNote}`
}

function openClaim(idx: number, type: ClaimType, faultOnly = false) {
  claimForm.idx = idx
  claimForm.type = type
  claimForm.reason = ''
  claimForm.faultOnly = faultOnly
  claimForm.returnCause = faultOnly ? 'FAULT' : ''
  const pref = preferRefundAccount()
  claimForm.refundBank = pref?.bank || ''
  claimForm.refundAccount = pref?.account || ''
  claimForm.refundHolder = applyRefundHolder(pref?.holder || '')
  if (type === 'RETURN') {
    const item = (order.value?.items || []).find((row) => row.idx === idx)
    claimReturnNote.value = item ? describeReturnFee(item) : describeReturnFee({})
  }
}

function closeClaim() {
  claimForm.idx = 0
  claimForm.reason = ''
  claimForm.returnCause = ''
  claimForm.faultOnly = false
  claimForm.refundBank = ''
  claimForm.refundAccount = ''
  claimForm.refundHolder = ''
  holderPrefillFromDepositor.value = false
}

function onPayAction() {
  if (tossMountFailed.value) {
    refreshPay()
    return
  }
  void onContinueTossPay()
}

async function onContinueTossPay() {
  if (!order.value || tossPaying.value || !tossReady.value) return
  tossPaying.value = true
  try {
    const first = order.value.items?.[0]?.name || '함우리몰 상품'
    const extra = Math.max(0, (order.value.itemCount || order.value.items?.length || 1) - 1)
    await requestTossPay({
      orderId: order.value.code,
      orderName: (extra > 0 ? `${first} 외 ${extra}건` : first).slice(0, 100),
      amount: order.value.totalAmount,
      customerName: order.value.receiver,
      customerEmail: order.value.memberEmail,
    })
  } catch {
    payHold.value = true
  } finally {
    tossPaying.value = false
  }
}

async function mountOrderToss() {
  const current = order.value
  if (!current || !tossEnabled.value) return
  if (current.status !== 'PENDING' || current.payMethod !== 'TOSS') return
  await nextTick()
  try {
    await mountToss(
      { methods: '#order-toss-method', agreement: '#order-toss-agreement' },
      current.totalAmount,
    )
    tossMountFailed.value = false
  } catch {
    tossMountFailed.value = true
  }
}

async function load() {
  loading.value = true
  tossMountFailed.value = false

  try {
    const result = await fetchMyOrder(code.value)
    order.value = result.order
    payment.value = result.payment
    fillTaxFormFromMember()
    tossEnabled.value = await fetchTossAccess().catch(() => false)
  } catch {
    order.value = null
    tossEnabled.value = false
  } finally {
    loading.value = false
  }

  await mountOrderToss()
}

async function onRequestTax() {
  if (!order.value || taxRequesting.value) return

  const taxError = taxPanelRef.value?.validate()
  if (taxError) {
    showError(taxError)
    return
  }

  const ok = await confirmDialog({
    title: '세금계산서 발행 요청',
    message: taxPanelRef.value?.confirmMessage() || '이 주문에 세금계산서 발행을 요청할까요?',
    confirmLabel: '요청하기',
    tone: 'primary',
  })
  if (!ok) return

  taxRequesting.value = true
  try {
    await taxPanelRef.value?.persist()
    const result = await requestMyTaxInvoice(order.value.code, {
      bizName: taxForm.bizName.trim(),
      bizNum: taxForm.bizNum.trim(),
      email: taxForm.email.trim(),
    })
    order.value = result.order
    showSuccess(result.message)
  } catch (error) {
    showError(error instanceof Error ? error.message : '세금계산서 요청에 실패했습니다.')
  } finally {
    taxRequesting.value = false
  }
}

onMounted(() => {
  void load()
})

onUnmounted(() => {
  document.body.classList.remove(STATEMENT_PRINT_BODY_CLASS)
})

const statusIcon = computed(() => {
  if (order.value?.status === 'PAID') return 'mdi-check-circle-outline'
  if (order.value?.status === 'CANCELED') return 'mdi-close-circle-outline'
  if (order.value?.status === 'PENDING' && order.value.payNotifiedAt) {
    return 'mdi-bank-check'
  }
  return 'mdi-clock-outline'
})

const statusDesc = computed(() => {
  const card = order.value?.payMethod === 'TOSS'
  if (order.value?.status === 'PAID') {
    return card
      ? '결제가 완료되어 판매업체에 주문이 전달되었습니다.'
      : '입금이 확인되어 판매업체에 주문이 전달되었습니다.'
  }
  if (order.value?.status === 'CANCELED') {
    return order.value.cancelReason || '주문이 취소되었습니다.'
  }
  if (order.value?.status === 'PENDING') {
    if (!card && order.value.payNotifiedAt) {
      return '입금 알림을 보냈습니다. 운영팀이 통장을 확인 중입니다.'
    }
    if (card) {
      return `${pendingExpireLabel(order.value.orderedAt)}. 오른쪽에서 카드·간편결제를 마쳐 주세요.`
    }
    return `${pendingExpireLabel(order.value.orderedAt)}. 오른쪽 입금 계좌에서 이체한 뒤 「입금했어요」를 눌러 주세요.`
  }
  return '안내된 계좌로 입금해 주세요.'
})

const timelineSteps = computed(() => {
  const card = order.value?.payMethod === 'TOSS'
  return [
  { key: 'pending', label: card ? '결제대기' : '입금대기' },
  { key: 'paid', label: card ? '결제완료' : '입금확인' },
  { key: 'ready', label: '출고준비' },
  { key: 'shipping', label: '배송중' },
  { key: 'done', label: '완료' },
  ]
})

function itemProgressRank(status: MallOrderItemStatus) {
  switch (status) {
    case 'WAITING':
      return 0
    case 'PAID':
      return 1
    case 'READY':
      return 2
    case 'SHIPPING':
      return 3
    case 'DELIVERED':
    case 'DONE':
      return 4
    case 'CANCEL_REQUEST':
      return 1
    case 'RETURN_REQUEST':
      return 4
    case 'CANCELED':
      return -1
    default:
      return 0
  }
}

const timelineIndex = computed(() => {
  if (!order.value || order.value.status === 'CANCELED') return 0
  if (order.value.status === 'PENDING') return 0

  const ranks = (order.value.items || [])
    .map((item) => itemProgressRank(item.status))
    .filter((rank) => rank >= 0)

  if (!ranks.length) return 1
  return Math.min(...ranks)
})

const timelineNote = computed(() => {
  if (!order.value) return ''
  if (order.value.status === 'PENDING') {
    if (order.value.payMethod === 'TOSS') {
      return `${pendingExpireLabel(order.value.orderedAt)}. 카드·간편결제를 마쳐 주세요. ${PENDING_EXPIRE_DAYS}일 안에 결제하지 않으면 주문이 자동 취소됩니다.`
    }
    if (order.value.payNotifiedAt) {
      return '입금 알림을 보냈습니다. 운영팀이 확인하면 출고 준비로 넘어갑니다.'
    }
    return `${pendingExpireLabel(order.value.orderedAt)}. 계좌이체 후 「입금했어요」를 눌러 주세요. ${PENDING_EXPIRE_DAYS}일 안에 누르지 않으면 주문이 자동 취소됩니다.`
  }

  const labels = timelineSteps.value[timelineIndex.value]?.label || ''
  const active = (order.value.items || []).filter((item) => item.status !== 'CANCELED')
  if (!active.length) return '처리 중인 상품이 없습니다.'
  if (timelineIndex.value >= 4) {
    const waiting = active.find((item) => item.status === 'DELIVERED' && item.deliveredAt)
    if (waiting?.deliveredAt) {
      return `배송이 완료되었습니다. ${autoConfirmLabel(waiting.deliveredAt)}.`
    }
    return '모든 상품 배송이 완료되었습니다.'
  }
  if (active.length === 1) return `현재 단계: ${labels}`
  return `여러 상품 중 가장 늦은 기준 · 현재 ${labels}`
})

async function copyText(value: string, field: 'account' | 'amount' | 'depositor', label: string) {
  const ok = await copyToClipboard(value)
  if (!ok) {
    showError(`${label} 복사에 실패했습니다. 직접 선택해 복사해 주세요.`)
    return
  }
  copiedField.value = field
  showSuccess(`${label}을(를) 복사했습니다.`)
  setTimeout(() => {
    if (copiedField.value === field) copiedField.value = ''
  }, 2000)
}

async function copyAccount() {
  if (!payment.value) return
  await copyText(payment.value.account.replace(/\s/g, ''), 'account', '계좌번호')
}

async function copyAmount() {
  if (!order.value) return
  await copyText(String(order.value.totalAmount), 'amount', '입금 금액')
}

async function copyDepositor() {
  if (!order.value) return
  const name = order.value.depositor || order.value.receiver
  await copyText(name, 'depositor', '입금자명')
}

async function onNotifyPayment() {
  if (notifying.value || !order.value || order.value.payNotifiedAt) return
  const ok = await confirmDialog({
    title: '입금 알림',
    message: '계좌로 입금하셨나요? 운영팀이 통장을 확인합니다.',
    detail: order.value
      ? `${order.value.code}\n입금액 ${formatPrice(order.value.totalAmount)}원`
      : '',
    confirmLabel: '입금했어요',
    tone: 'primary',
  })
  if (!ok) return

  notifying.value = true
  try {
    const result = await notifyMyPayment(code.value)
    order.value = result.order
    showSuccess(result.message || '입금 완료를 알려 주셨습니다.')
  } catch (error) {
    showError(error)
  } finally {
    notifying.value = false
  }
}

async function onReorder() {
  if (reordering.value || !order.value?.items?.length) return
  reordering.value = true
  try {
    for (const item of order.value.items) {
      if (!item.productCode || item.status === 'CANCELED') continue
      await addItem(
        item.productCode,
        item.qty,
        item.option ? { name: item.option, extra: 0 } : undefined,
      )
    }
    showSuccess('장바구니에 담았습니다.')
    router.push('/mall/cart')
  } catch (error) {
    showError(error)
  } finally {
    reordering.value = false
  }
}

async function onCancel() {
  if (canceling.value) return
  const ok = await confirmDialog({
    title: '주문 취소',
    message: order.value?.payMethod === 'TOSS'
      ? '주문을 취소할까요? 결제 전에만 취소할 수 있습니다.'
      : '주문을 취소할까요? 입금 전에만 취소할 수 있습니다.',
    confirmLabel: '주문 취소',
    tone: 'danger',
  })
  if (!ok) return

  canceling.value = true

  try {
    await cancelMyOrder(code.value)
    await load()
    showSuccess('주문을 취소했습니다.')
  } catch (error) {
    showError(error)
  } finally {
    canceling.value = false
  }
}

async function submitClaim() {
  if (claiming.value || !claimForm.idx || !canSubmitClaim.value) return

  claiming.value = claimForm.idx
  try {
    await requestOrderItemClaim(claimForm.idx, {
      type: claimForm.type,
      reason: claimForm.reason,
      returnCause: claimForm.type === 'RETURN' ? claimForm.returnCause : '',
      refundBank: claimForm.refundBank,
      refundAccount: claimForm.refundAccount,
      refundHolder: claimForm.refundHolder,
    })
    const claimType = claimForm.type
    saveLastRefundAccount(
      claimForm.refundBank,
      claimForm.refundAccount,
      claimForm.refundHolder,
    )
    closeClaim()
    await load()
    showSuccess(
      claimType === 'CANCEL'
        ? order.value?.payMethod === 'TOSS'
          ? '환불신청이 접수되었습니다. 주문이 취소되었고 결제했던 카드·간편결제로 환불됩니다.'
          : '환불신청이 접수되었습니다. 주문이 취소되었고 운영팀이 환불을 진행합니다.'
        : order.value?.payMethod === 'TOSS'
          ? '반품 요청을 보냈습니다. 승인되면 결제했던 카드·간편결제로 환불됩니다.'
          : '반품 요청을 보냈습니다.',
    )
  } catch (error) {
    showError(error)
  } finally {
    claiming.value = 0
  }
}

async function onConfirmPurchase(idx: number) {
  if (confirming.value) return
  const ok = await confirmDialog({
    title: '구매 확정',
    message: '구매를 확정할까요? 확정 뒤에는 단순 변심 반품은 할 수 없고, 상품 하자·오배송만 요청할 수 있습니다.',
    confirmLabel: '구매 확정',
    tone: 'primary',
  })
  if (!ok) return

  confirming.value = idx
  try {
    await confirmOrderItemPurchase(idx)
    await load()
    showSuccess('구매를 확정했습니다.')
  } catch (error) {
    showError(error)
  } finally {
    confirming.value = 0
  }
}

async function copyInvoice(invoice: string) {
  const ok = await copyToClipboard(invoice.replace(/\s/g, ''))
  if (ok) {
    showSuccess('송장번호를 복사했습니다.')
    return
  }
  showError('송장번호 복사에 실패했습니다. 직접 선택해 복사해 주세요.')
}

function openRefundAccount(item: MallOrderItem) {
  refundForm.idx = item.idx
  const pref = preferRefundAccount()
  refundForm.bank = item.refundBank || pref?.bank || ''
  refundForm.account = item.refundAccount || pref?.account || ''
  refundForm.holder = applyRefundHolder(item.refundHolder || pref?.holder || '')
}

async function submitRefundAccount() {
  if (!refundForm.idx || refundSaving.value) return
  refundSaving.value = refundForm.idx
  try {
    await updateOrderItemRefundAccount(refundForm.idx, {
      refundBank: refundForm.bank,
      refundAccount: refundForm.account,
      refundHolder: refundForm.holder,
    })
    saveLastRefundAccount(refundForm.bank, refundForm.account, refundForm.holder)
    refundForm.idx = 0
    await load()
    showSuccess('환불 계좌를 저장했습니다.')
  } catch (error) {
    showError(error)
  } finally {
    refundSaving.value = 0
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/* 목록에서 넘어온 뒤 주소가 바뀌면 다시 읽는다 */
router.afterEach((to) => {
  if (to.name === 'mall-order-detail' && to.params.code !== order.value?.code) {
    load()
  }
})
</script>

<style lang="scss" src="./MallOrderDetailPage.scss"></style>
